import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, type ApolloClient } from '@apollo/client';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import {
    GetAutoApplyScreenerPrefillStateDocument,
    type GetAutoApplyScreenerPrefillStateQuery,
    type GetAutoApplyScreenerPrefillStateQueryVariables,
} from './__generated__/ApplicationTabContent.generated';
import { hasPrefilledScreenerAnswer } from '../questions/screenerQuestionUtils';
import type { ScreenerQuestionContextQuestion } from '../../../__generated__/graphql-base-types';
import { ROOT_TRACKING_KEY_HEADER } from '../../../lib/apollo/headers';

/** Whether the flow has a `/questions` step for this draft (i.e. it wasn't fully prefilled). */
export const draftHasQuestionsStep = (
    allSteps: Array<{ routes: string[] }>,
    draftId: string | undefined,
): boolean =>
    !!draftId && allSteps.some((step) => step.routes.includes(`/auto-apply/${draftId}/questions`));

/**
 * Fetches the draft's screener prefill and decides whether any question was AI-prefilled. Assumes the
 * caller has already gated on `draftHasQuestionsStep` + `iaUid`. Reads the shared `cache-first` prefill
 * query (a cache hit when it's already been read for this draft) and fails open on any error.
 *
 * `ScreenerQuestionContextQuestion` is pinned to `keyFields: false` in the cache (client.ts), so the
 * non-unique screener ids can't collapse questions across (or within) drafts on the cached read.
 */
export const fetchPrefillDisclaimer = async (
    client: ApolloClient<object>,
    draftId: string,
    iaUid: string,
): Promise<boolean> => {
    try {
        const { data, error } = await client.query<
            GetAutoApplyScreenerPrefillStateQuery,
            GetAutoApplyScreenerPrefillStateQueryVariables
        >({
            query: GetAutoApplyScreenerPrefillStateDocument,
            variables: { input: { draftId } },
            fetchPolicy: 'cache-first',
            context: { headers: { [ROOT_TRACKING_KEY_HEADER]: iaUid } },
        });
        if (error) return true; // fail open: keep the disclaimer
        const questions = (data.smartApplyContext?.screenerQuestionContext?.screenerQuestions ??
            []) as ScreenerQuestionContextQuestion[];
        return hasPrefilledScreenerAnswer(questions);
    } catch {
        return true; // fail open (network error)
    }
};

/**
 * Resolves the AI-autofill disclaimer flag for a single draft, imperatively — the shared source of
 * truth for the decision. Returns `true` (keep the disclaimer) for a fully-prefilled draft (no
 * questions step), when the tracking key is missing, or on any query error; otherwise reflects whether
 * any question was AI-prefilled. Used by ConfirmationPage (which needs it per draft, in an async loop)
 * and, reactively, by {@link useShowAiAutofillDisclaimer}.
 */
export const resolveShowAiAutofillDisclaimer = async (
    client: ApolloClient<object>,
    allSteps: Array<{ routes: string[] }>,
    draftId: string | undefined,
    iaUid: string | undefined,
): Promise<boolean> => {
    if (!draftHasQuestionsStep(allSteps, draftId)) return true;
    if (!draftId || !iaUid) return true; // fail open: no tracking-key header to gate the query on
    return fetchPrefillDisclaimer(client, draftId, iaUid);
};

/**
 * Whether the review preview should embed the server's AI-autofill disclaimer.
 *
 * - `true` — at least one screener question was prefilled (AI actually autofilled something), or the
 *   draft has no questions step (fully prefilled), or we fail open on error.
 * - `false` — the draft went through its `/questions` step and nothing was prefilled, so every answer
 *   was entered manually.
 * - `undefined` — still resolving (flow step list or the prefill query); callers should defer preview
 *   generation until it settles so the mutation never runs with a stale value.
 *
 * The reactive wrapper around {@link resolveShowAiAutofillDisclaimer}: the `flowLoading` /
 * `hasQuestionsStep` / `iaUid` gates resolve synchronously (so a fully-prefilled or key-less draft
 * settles without a fetch), and only the prefill query itself is awaited in an effect.
 */
export const useShowAiAutofillDisclaimer = (
    draftId: string | undefined,
    iaUid: string | undefined,
): boolean | undefined => {
    const client = useApolloClient();
    const { allSteps, loading: flowLoading } = usePageFlow();

    const hasQuestionsStep = useMemo(
        () => draftHasQuestionsStep(allSteps, draftId),
        [allSteps, draftId],
    );

    const [decided, setDecided] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        // The synchronous fast-paths below need no fetch; only fetch once actually gated in.
        if (flowLoading || !hasQuestionsStep || !draftId || !iaUid) return;
        let active = true;
        setDecided(undefined); // re-defer if the inputs change
        void fetchPrefillDisclaimer(client, draftId, iaUid).then((value) => {
            if (active) setDecided(value);
        });
        return () => {
            active = false;
        };
    }, [client, flowLoading, hasQuestionsStep, draftId, iaUid]);

    if (flowLoading) return undefined;
    if (!hasQuestionsStep) return true; // fully prefilled — keep the disclaimer, no query
    if (!iaUid) return true; // no tracking-key header to gate the query on — fail open, no query
    return decided; // undefined until the effect settles
};
